import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Tabs,
  Tab,
  Spinner,
} from "react-bootstrap";
import { API_URL, BASE_URL } from "../../services/api";
import axios from "axios";
import { useTheme } from "../../contexts/ThemeContext";
import {
  extractGoogleMapsEmbedUrl,
  isValidGoogleMapsEmbedUrl,
} from "../../utils/googleMapsEmbed";
import "./SettingsManagement.css";
import "./Dashboard.css";
import defaultLogo from "../../assets/default-logo.png"; // Siapkan logo default jika belum ada

const SOCIAL_FIELDS = [
  ["facebook_url", "Facebook", "https://www.facebook.com/lembaga"],
  ["twitter_url", "Twitter / X", "https://x.com/lembaga"],
  ["instagram_url", "Instagram", "https://www.instagram.com/lembaga"],
  ["youtube_url", "YouTube", "https://www.youtube.com/@lembaga"],
];

const SettingsManagement = () => {
  const { refreshTheme, updateThemeColor, refreshLogo } = useTheme();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [unavailableFooterFields, setUnavailableFooterFields] = useState([]);

  const [formData, setFormData] = useState({
    id_lp: "",
    nama_lembaga: "",
    jenis_lembaga: "",
    alamat: "",
    desa: "",
    kode_pos: "",
    nama_pimpinan: "",
    no_hp: "",
    kontak_person: "",
    no_hp_kontak_person: "",
    email: "",
    motto: "",
    facebook_url: "",
    twitter_url: "",
    instagram_url: "",
    youtube_url: "",
    jam_operasional: "",
    google_maps_embed: "",
    no_register: "",
    status: "Aktif",
    warna_tema: "#10b981",
    warna_tema_hover: "#059669",
    warna_tema_light: "#d1fae5",
    warna_tema_dark: "#047857",
  });

  const [logoUrl, setLogoUrl] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState("");

  useEffect(() => {
    // Ambil URL logo dari /api/config
    axios.get("/api/config").then((res) => {
      setLogoUrl(res.data?.LOGO_URL ? `${BASE_URL}${res.data.LOGO_URL}` : "");
    });
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/lp/settings`);
      if (response.data) {
        setUnavailableFooterFields(response.data.unavailable_footer_fields || []);
        setFormData({
          id_lp: response.data.id_lp || "",
          nama_lembaga: response.data.nama_lembaga || "",
          jenis_lembaga: response.data.jenis_lembaga || "",
          alamat: response.data.alamat || "",
          desa: response.data.desa || "",
          kode_pos: response.data.kode_pos || "",
          nama_pimpinan: response.data.nama_pimpinan || "",
          no_hp: response.data.no_hp || "",
          kontak_person: response.data.kontak_person || "",
          no_hp_kontak_person: response.data.no_hp_kontak_person || "",
          email: response.data.email || "",
          motto: response.data.motto || "",
          facebook_url: response.data.facebook_url || "",
          twitter_url: response.data.twitter_url || "",
          instagram_url: response.data.instagram_url || "",
          youtube_url: response.data.youtube_url || "",
          jam_operasional: response.data.jam_operasional || "",
          google_maps_embed: response.data.google_maps_embed || "",
          no_register: response.data.no_register || "",
          status: response.data.status || "Aktif",
          warna_tema: response.data.warna_tema || "#10b981",
          warna_tema_hover: response.data.warna_tema_hover || "#059669",
          warna_tema_light: response.data.warna_tema_light || "#d1fae5",
          warna_tema_dark: response.data.warna_tema_dark || "#047857",
        });
      }
    } catch (err) {
      // If no data exists yet, generate a new GUID for id_lp
      if (err.response?.status === 404) {
        setFormData((prev) => ({
          ...prev,
          id_lp: generateGUID(),
        }));
      } else {
        setError("Gagal memuat pengaturan: " + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const generateGUID = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0,
          v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyDefault = () => {
    const defaults = {
      warna_tema: "#10b981",
      warna_tema_hover: "#059669",
      warna_tema_light: "#d1fae5",
      warna_tema_dark: "#047857",
    };

    setFormData((prev) => ({
      ...prev,
      ...defaults,
    }));

    updateThemeColor({
      primary: defaults.warna_tema,
      hover: defaults.warna_tema_hover,
      light: defaults.warna_tema_light,
      dark: defaults.warna_tema_dark,
    });

    setSuccess("Tema dikembalikan ke default hijau");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      // If no id_lp exists, generate one
      const dataToSend = {
        ...formData,
        id_lp: formData.id_lp || generateGUID(),
        google_maps_embed: extractGoogleMapsEmbedUrl(formData.google_maps_embed),
      };

      const response = await axios.put(`${API_URL}/lp/settings`, dataToSend);
      const unavailable = response.data.unavailable_footer_fields || [];
      setUnavailableFooterFields(unavailable);

      setSuccess(unavailable.length
        ? "Pengaturan yang didukung berhasil disimpan. Sebagian pengaturan footer belum tersedia."
        : "Pengaturan berhasil disimpan!");

      // Refresh theme color globally
      await refreshTheme();

      // Refresh data
      setTimeout(() => {
        fetchSettings();
        setSuccess("");
      }, 2000);
    } catch (err) {
      setError("Gagal menyimpan pengaturan: " + (err.response?.data?.message || err.message));
      console.error("Error:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (e) => {
    setLogoError("");
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setLogoError("File harus berupa gambar");
      return;
    }
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);
      const res = await axios.post("/api/logo", formData);
      const uploadedUrl = res.data?.logoUrl
        ? `${BASE_URL}${res.data.logoUrl}`
        : "";
      // force refresh dengan timestamp agar cache browser ter-bypass
      setLogoUrl(uploadedUrl ? `${uploadedUrl}?${Date.now()}` : "");
      setLogoError("");
      // Perbarui logo global (Header, Footer, sidebar Admin)
      if (refreshLogo) refreshLogo();
    } catch (err) {
      setLogoError(
        "Gagal upload logo: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setLogoUploading(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" variant="success" />
        <p className="mt-2">Memuat pengaturan...</p>
      </Container>
    );
  }

  return (
    <Container className="settings-management mt-4">
      {/* Logo Tenant */}
      <div className="text-center mb-4">
        <img
          src={logoUrl || defaultLogo}
          alt="Logo Tenant"
          onError={(e) => {
            e.currentTarget.src = defaultLogo;
          }}
          style={{
            maxHeight: 80,
            maxWidth: 200,
            objectFit: "contain",
            background: "#fff",
            borderRadius: 8,
            border: "1px solid #eee",
            padding: 8,
          }}
        />
        <div className="mt-2">
          <Form.Label
            htmlFor="logo-upload"
            className="btn btn-outline-primary btn-sm mb-0"
          >
            {logoUploading ? "Mengupload..." : "Ganti Logo"}
            <Form.Control
              id="logo-upload"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleLogoChange}
              disabled={logoUploading}
            />
          </Form.Label>
        </div>
        {logoError && <div className="text-danger small mt-1">{logoError}</div>}
      </div>

      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-cogs"></i>
            </div>
            <div>
              <h1 className="page-header-title">Pengaturan LP3H</h1>
              <p className="page-header-subtitle">
                Kelola informasi dan pengaturan lembaga
              </p>
            </div>
          </div>
        </div>
      </div>

      {success && (
        <Alert variant="success" onClose={() => setSuccess("")} dismissible>
          {success}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Tabs defaultActiveKey="general" id="settings-tabs" className="mb-0">
            <Tab
              eventKey="general"
              title={
                <span>
                  <i className="fas fa-building me-2"></i>Informasi Umum
                </span>
              }
            >
              <div className="tab-content-wrapper p-4">
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Nama Lembaga <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="nama_lembaga"
                          value={formData.nama_lembaga}
                          onChange={handleChange}
                          placeholder="LP3H Matlaul Anwar Sinar Gading"
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Jenis Lembaga</Form.Label>
                        <Form.Control
                          type="text"
                          name="jenis_lembaga"
                          value={formData.jenis_lembaga}
                          onChange={handleChange}
                          placeholder="Lembaga Pendamping"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Alamat <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="alamat"
                      value={formData.alamat}
                      onChange={handleChange}
                      placeholder="Alamat lengkap lembaga"
                      required
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Kelurahan/Desa</Form.Label>
                        <Form.Control
                          type="text"
                          name="desa"
                          value={formData.desa}
                          onChange={handleChange}
                          placeholder="Nama kelurahan/desa"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Kode Pos</Form.Label>
                        <Form.Control
                          type="text"
                          name="kode_pos"
                          value={formData.kode_pos}
                          onChange={handleChange}
                          placeholder="35148"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Nama Pimpinan</Form.Label>
                        <Form.Control
                          type="text"
                          name="nama_pimpinan"
                          value={formData.nama_pimpinan}
                          onChange={handleChange}
                          placeholder="Nama pimpinan lembaga"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          No. HP/Telepon <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="no_hp"
                          value={formData.no_hp}
                          onChange={handleChange}
                          placeholder="+6282182226942"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Kontak Person</Form.Label>
                        <Form.Control
                          type="text"
                          name="kontak_person"
                          value={formData.kontak_person}
                          onChange={handleChange}
                          placeholder="Nama kontak person"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>No. HP Kontak Person</Form.Label>
                        <Form.Control
                          type="text"
                          name="no_hp_kontak_person"
                          value={formData.no_hp_kontak_person}
                          onChange={handleChange}
                          placeholder="No. HP kontak person"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Email <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="email@lp3h.com"
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>No. Register</Form.Label>
                        <Form.Control
                          type="text"
                          name="no_register"
                          value={formData.no_register}
                          onChange={handleChange}
                          placeholder="No. registrasi lembaga"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Jam Operasional</Form.Label>
                    <Form.Control
                      type="text"
                      name="jam_operasional"
                      value={formData.jam_operasional}
                      onChange={handleChange}
                      placeholder="Senin - Jumat: 08:00 - 17:00 WIB"
                    />
                    <Form.Text className="text-muted">
                      Ditampilkan di halaman Kontak publik.
                    </Form.Text>
                  </Form.Group>

                  <h5 className="mt-4">Motto dan Media Sosial</h5>
                  {unavailableFooterFields.length > 0 && (
                    <Alert variant="warning">
                      Sebagian isian footer belum tersedia untuk lembaga ini.
                      Hubungi administrator untuk mengaktifkannya. Pengaturan lainnya tetap dapat disimpan.
                    </Alert>
                  )}
                  <Form.Group className="mb-3" controlId="settings-motto">
                    <Form.Label>Motto</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="motto"
                      value={formData.motto}
                      onChange={handleChange}
                      maxLength={500}
                      disabled={unavailableFooterFields.includes("motto")}
                      placeholder="Motto lembaga Anda"
                    />
                    <Form.Text className="text-muted">
                      Ditampilkan di footer di bawah nama LPH. Jika kosong, deskripsi bawaan tetap ditampilkan.
                    </Form.Text>
                  </Form.Group>
                  <Row>
                    {SOCIAL_FIELDS.map(([field, label, placeholder]) => (
                      <Col md={6} key={field}>
                        <Form.Group className="mb-3" controlId={`settings-${field}`}>
                          <Form.Label>URL {label}</Form.Label>
                          <Form.Control
                            type="url"
                            name={field}
                            value={formData[field]}
                            onChange={handleChange}
                            maxLength={500}
                            pattern="https?://.*"
                            disabled={unavailableFooterFields.includes(field)}
                            placeholder={placeholder}
                          />
                        </Form.Group>
                      </Col>
                    ))}
                  </Row>
                  <p className="text-muted small">Gunakan URL lengkap dengan https://. Kosongkan untuk menyembunyikan ikon media sosial tersebut.</p>

                  <Form.Group className="mb-3">
                    <Form.Label>Lokasi Google Maps</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="google_maps_embed"
                      value={formData.google_maps_embed}
                      onChange={handleChange}
                      placeholder='Tempel kode embed dari Google Maps, contoh: &lt;iframe src="https://www.google.com/maps/embed?pb=..." ...&gt;&lt;/iframe&gt;'
                    />
                    <Form.Text className="text-muted">
                      Buka Google Maps → cari alamat → Share → Embed a map →
                      salin kode HTML atau URL embed. Peta ditampilkan di
                      halaman Kontak.
                    </Form.Text>
                    {isValidGoogleMapsEmbedUrl(
                      extractGoogleMapsEmbedUrl(formData.google_maps_embed),
                    ) && (
                      <div className="settings-map-preview mt-3">
                        <p className="text-muted small mb-2">Pratinjau peta:</p>
                        <iframe
                          title="Pratinjau Google Maps"
                          src={extractGoogleMapsEmbedUrl(
                            formData.google_maps_embed,
                          )}
                          width="100%"
                          height="280"
                          style={{ border: 0, borderRadius: "8px" }}
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="Aktif">Aktif</option>
                      <option value="Tidak Aktif">Tidak Aktif</option>
                    </Form.Select>
                  </Form.Group>

                  <div className="d-flex justify-content-end gap-2">
                    <Button variant="success" type="submit" disabled={saving}>
                      {saving ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Menyimpan...
                        </>
                      ) : (
                        "Simpan Perubahan"
                      )}
                    </Button>
                  </div>
                </Form>
              </div>
            </Tab>

            <Tab
              eventKey="theme"
              title={
                <span>
                  <i className="fas fa-palette me-2"></i>Tema & Tampilan
                </span>
              }
            >
              <div className="tab-content-wrapper p-4">
                <Form onSubmit={handleSubmit}>
                  <h5 className="mb-3">Pengaturan Warna Tema</h5>
                  <p className="text-muted">
                    Pilih warna tema utama untuk website LP3H
                  </p>

                  <Form.Group className="mb-3">
                    <Form.Label>Warna Tema</Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      <Form.Control
                        type="color"
                        name="warna_tema"
                        value={formData.warna_tema}
                        onChange={handleChange}
                        style={{ width: "100px", height: "50px" }}
                      />
                      <Form.Control
                        type="text"
                        name="warna_tema"
                        value={formData.warna_tema}
                        onChange={handleChange}
                        placeholder="#10b981"
                        style={{ width: "150px" }}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Default: #10b981 (Green). Gunakan format HEX color.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Warna Hover</Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      <Form.Control
                        type="color"
                        name="warna_tema_hover"
                        value={formData.warna_tema_hover}
                        onChange={handleChange}
                        style={{ width: "100px", height: "50px" }}
                      />
                      <Form.Control
                        type="text"
                        name="warna_tema_hover"
                        value={formData.warna_tema_hover}
                        onChange={handleChange}
                        placeholder="#059669"
                        style={{ width: "150px" }}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Warna saat hover pada tombol dan elemen utama.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Warna Light</Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      <Form.Control
                        type="color"
                        name="warna_tema_light"
                        value={formData.warna_tema_light}
                        onChange={handleChange}
                        style={{ width: "100px", height: "50px" }}
                      />
                      <Form.Control
                        type="text"
                        name="warna_tema_light"
                        value={formData.warna_tema_light}
                        onChange={handleChange}
                        placeholder="#d1fae5"
                        style={{ width: "150px" }}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Warna latar terang untuk alert, tabel, badge.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Warna Dark</Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      <Form.Control
                        type="color"
                        name="warna_tema_dark"
                        value={formData.warna_tema_dark}
                        onChange={handleChange}
                        style={{ width: "100px", height: "50px" }}
                      />
                      <Form.Control
                        type="text"
                        name="warna_tema_dark"
                        value={formData.warna_tema_dark}
                        onChange={handleChange}
                        placeholder="#047857"
                        style={{ width: "150px" }}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Warna gelap untuk aktif/klik, state aktif.
                    </Form.Text>
                  </Form.Group>

                  <div className="theme-preview mt-4 p-4 border rounded">
                    <h6>Preview Warna:</h6>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                      <div
                        className="p-3 rounded text-white"
                        style={{ backgroundColor: formData.warna_tema }}
                      >
                        Primary
                      </div>
                      <div
                        className="p-3 rounded text-white"
                        style={{ backgroundColor: formData.warna_tema_hover }}
                      >
                        Hover
                      </div>
                      <div
                        className="p-3 rounded"
                        style={{ backgroundColor: formData.warna_tema_light }}
                      >
                        Light
                      </div>
                      <div
                        className="p-3 rounded text-white"
                        style={{ backgroundColor: formData.warna_tema_dark }}
                      >
                        Dark
                      </div>
                      <Button
                        variant="success"
                        style={{
                          backgroundColor: formData.warna_tema,
                          borderColor: formData.warna_tema,
                        }}
                      >
                        Button Example
                      </Button>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between gap-2 mt-4">
                    <Button
                      variant="outline-secondary"
                      onClick={handleApplyDefault}
                      disabled={saving}
                    >
                      Apply Default
                    </Button>

                    <Button variant="success" type="submit" disabled={saving}>
                      {saving ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Menyimpan...
                        </>
                      ) : (
                        "Simpan Perubahan"
                      )}
                    </Button>
                  </div>
                </Form>
              </div>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SettingsManagement;
